/* eslint-disable react/display-name */
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import { forwardRef, useDeferredValue, useEffect, useImperativeHandle, useState } from 'react';
import Modal, { ModalProps } from 'rsuite/lib/Modal';
import { ButtonPrimary, InputBox } from '../../../../../../../../components';
import { AlertWithTimestamp } from '../../../../../../../../components/AlertWithTimestamp';
import { LoadingSpinerArea } from '../../../../../../../../components/LoadingSpinner';
import { selectorSuppliersExhibitionType, useSuppliersExhibitionStore } from '../../../../../../../../store/useSuppliersExhibitionStore';
import { checkIfIsLoading } from '../../../../../../../../utils';
import { extractApiErrorMessage } from '../../../../../../../../utils/extractApiErrorMessage';
import { getNegociacoesSalvas } from '../../../NegociacaoFornecedor.services';

export type AdicionarProdutoNegociacaoProps = ModalProps & {
  onSubmit?: (formData: { id: string }) => void;
  onHide?: () => void;
};

export type AdicionarProdutoRefProps = {
  reset: () => void;
};

export const AdicionarProdutoNegociacaoModal = forwardRef<AdicionarProdutoRefProps, AdicionarProdutoNegociacaoProps>(
  ({ className = '', onHide, onSubmit, ...props }, ref) => {
    const [selectedNegotiation, setSelectedNegotiation] = useState('');
    const [query, setQuery] = useState('');
    const deferredQuery = useDeferredValue(query);

    const suppliersExhibitionType = useSuppliersExhibitionStore(selectorSuppliersExhibitionType);

    useImperativeHandle(ref, () => ({
      reset: () => setSelectedNegotiation(''),
    }));

    const handleSearch = (value: string) => setQuery(value);

    // O estado de falha passou a ser lido aqui. Antes, esta consulta observava apenas se estava
    // buscando: uma falha ao carregar as negociações salvas deixava a lista vazia, com a MESMA frase que
    // aparece quando o comprador de fato não tem negociação salva. Os dois casos desenhavam igual, e num
    // deles o comprador conclui a coisa errada — que não tem destino para o produto, quando o que houve
    // foi erro.
    const { data, fetchStatus, isError } = useQuery({
      initialData: [],
      queryKey: ['get-saved-negotiations', deferredQuery, suppliersExhibitionType],
      queryFn: () => getNegociacoesSalvas(deferredQuery, suppliersExhibitionType),
      enabled: !!props.show,
      // O mesmo canal que o módulo já usa em falha de salvar, e não um segundo padrão de erro nesta
      // tela. Ele extrai a mensagem da resposta pelo caminho conhecido e recua para uma frase própria —
      // corpo de resposta e pilha não chegam à tela.
      onError: error => {
        AlertWithTimestamp({ message: extractApiErrorMessage(error) ?? 'Erro ao carregar as negociações salvas' });
      },
    });

    useEffect(() => {
      if (props.show) {
        setSelectedNegotiation('');
      }
    }, [props.show]);

    const isLoading = checkIfIsLoading(fetchStatus);

    // Em falha, a lista não diz nada: quem fala é o aviso flutuante acima. "Nenhuma negociação
    // encontrada" é a frase do caso legítimo, e nenhum texto novo entra aqui — o que faltava era o
    // estado de erro, não redação nova.
    const emptyDestinationListText = () => {
      if (isError) return '';

      return isLoading ? 'Carregando negociações...' : 'Nenhuma negociação encontrada';
    };

    return (
      <Modal className={classNames('info-modal', className)} onHide={onHide} {...props}>
        <LoadingSpinerArea height="100%" size="md" area="negotiation/add-product-to-negotiation" />
        <form onSubmit={e => e.preventDefault()}>
          <Modal.Header>
            <Modal.Title className="font-size-200-semibold">Adicionar à negociação salva</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <InputBox>
              <InputBox.Label>Selecione uma negociação para adicionar o produto</InputBox.Label>
              <InputBox.Select
                data={isLoading ? [] : data}
                value={selectedNegotiation}
                onChange={setSelectedNegotiation}
                onSearch={handleSearch}
                placeholder="Selecione uma opção"
                locale={{
                  searchPlaceholder: 'Busque uma negociação',
                  noResultsText: emptyDestinationListText(),
                }}
                onClean={() => handleSearch('')}
              />
            </InputBox>
          </Modal.Body>
          <Modal.Footer>
            <ButtonPrimary skin="blue" theme="ghost" type="button" onClick={onHide}>
              FECHAR
            </ButtonPrimary>
            <ButtonPrimary
              skin="blue"
              theme="filled"
              type="submit"
              onClick={() =>
                onSubmit?.({
                  id: selectedNegotiation,
                })
              }
              disabled={!selectedNegotiation?.length}
            >
              SALVAR
            </ButtonPrimary>
          </Modal.Footer>
        </form>
        <LoadingSpinerArea size="sm" area="saved-filter" />
      </Modal>
    );
  },
);
